import { useCallback, useEffect } from "react";

import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDealActions } from "store";
import { IDataExtractionTag } from "store/types";

export const useExtractionParameters = (fileId: number) => {
  const { getFileTagsAction, deleteFileTagAction } = useDealActions();
  const {
    action: fetchTags,
    data: parameters = [],
    setData: setParameters,
    loading
  } = useActionHook(getFileTagsAction);
  const { action: deleteTag } = useActionHook(deleteFileTagAction);

  useEffect(() => {
    setParameters([]);
    fetchTags(fileId);
  }, [fileId]);

  const deleteParameter = useCallback(
    async (id: number) => {
      await deleteTag(fileId, id);
      setParameters((prev: IDataExtractionTag[] | undefined) => (prev || []).filter((p) => p.id !== id));
    },
    [fileId, deleteTag, setParameters]
  );

  const updateTag = useCallback(
    (updatedTag: IDataExtractionTag) => {
      setParameters((prev: IDataExtractionTag[] | undefined) =>
        (prev || []).map((p) => (p.id === updatedTag.id ? updatedTag : p))
      );
    },
    [setParameters]
  );

  const addTag = useCallback(
    (newTag: IDataExtractionTag) => {
      setParameters((prev: IDataExtractionTag[] | undefined) => [...(prev || []), newTag]);
    },
    [setParameters]
  );

  const refetch = useCallback(() => {
    fetchTags(fileId);
  }, [fileId, fetchTags]);

  const silentRefetch = useCallback(async () => {
    const data = await getFileTagsAction(fileId);
    if (data) setParameters(data);
  }, [fileId, getFileTagsAction, setParameters]);

  return {
    parameters: (parameters || []) as IDataExtractionTag[],
    loading,
    deleteParameter,
    updateTag,
    addTag,
    refetch,
    silentRefetch
  };
};
